import { Box, Container, Typography, useTheme } from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import {
  ImposterContentInfo,
  ImposterPassStep,
  ImposterRevealStep,
  ImposterRulesStep,
  ImposterSetupStep,
} from "../components/imposter";
import { GameSessionModeCategoryContent } from "../types/api";
import { apiClient } from "../utils/api";
import { MixpanelEvents, trackEvent } from "../utils/mixpanel";

type Step = "setup" | "reveal" | "pass" | "rules";

const ImposterPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const { identifier } = useParams<{ identifier: string }>();
  const [content, setContent] = useState<GameSessionModeCategoryContent | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [numPlayers, setNumPlayers] = useState(6);
  const numImposters = 1; // Always 1, non-changeable
  const [step, setStep] = useState<Step>("setup");
  const [playerRoles, setPlayerRoles] = useState<boolean[]>([]); // true = imposter, false = not imposter
  const [steps, setSteps] = useState<string[]>([]); // Array like ["p1 waiting", "p1 reveal", "p2 waiting", "p2 reveal", ...]
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [swipeStartX, setSwipeStartX] = useState<number | null>(null);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const isProcessingRef = useRef(false);

  useEffect(() => {
    if (!identifier) return;
    trackEvent(MixpanelEvents.IMPOSTER_SHARE_PAGE_VIEWED, {});
  }, [identifier]);

  // Fetch content
  useEffect(() => {
    const fetchContent = async () => {
      if (!identifier) {
        setError(t("common.error"));
        setLoading(false);
        return;
      }

      try {
        const data = await apiClient.getContentByIdentifier(identifier);
        setContent(data);

      } catch (err: any) {
        setError(err?.response?.data?.detail || t("common.error"));
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, [identifier, t]);

  // Generate steps array and assign roles when play is clicked
  const handlePlay = () => {
    // Randomly assign imposter roles
    const roles: boolean[] = new Array(numPlayers).fill(false);
    const imposterIndices: number[] = [];

    // Select random players to be imposters
    while (imposterIndices.length < numImposters) {
      const randomIndex = Math.floor(Math.random() * numPlayers);
      if (!imposterIndices.includes(randomIndex)) {
        imposterIndices.push(randomIndex);
        roles[randomIndex] = true;
      }
    }

    setPlayerRoles(roles);

    // Generate steps array: ["p1 waiting", "p1 reveal", "p2 waiting", "p2 reveal", ...]
    const newSteps: string[] = [];
    for (let i = 1; i <= numPlayers; i++) {
      newSteps.push(`p${i} waiting`);
      newSteps.push(`p${i} reveal`);
    }
    setSteps(newSteps);
    setCurrentStepIndex(0);
    setStep("reveal");
  };

  // Parse current step to get player number and state
  const currentStep = steps[currentStepIndex] || "";
  const stepMatch = currentStep.match(/p(\d+) (waiting|reveal)/);
  const currentPlayerNumber = stepMatch ? parseInt(stepMatch[1], 10) : 0;
  const currentPlayerIndex = currentPlayerNumber > 0 ? currentPlayerNumber - 1 : 0;
  const isWaiting = currentStep.includes("waiting");
  const isCurrentPlayerImposter = playerRoles[currentPlayerIndex] || false;

  // Helper function to get image URL
  const getImageUrl = (imagePath: string | undefined | null): string | null => {
    if (!imagePath) {
      return null;
    }
    // If it's already a full URL, return as is
    if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
      return imagePath;
    }
    // Otherwise, prefix with app base URL for relative media paths
    const API_BASE_URL =
      process.env.REACT_APP_API_URL || "http://localhost:8000";
    const fullUrl = `${API_BASE_URL}${imagePath}`;
    return fullUrl;
  };

  // Select image based on language preference
  const imagePath =
    i18n.language === "ar"
      ? content?.content.image_ar || content?.content.image
      : content?.content.image || content?.content.image_ar;
  const contentImage = getImageUrl(imagePath || null);

  // Swipe handlers
  const handleSwipeStart = (clientX: number) => {
    if (isProcessingRef.current) return;
    setSwipeStartX(clientX);
    setSwipeOffset(0);
  };

  const handleSwipeMove = useCallback(
    (clientX: number) => {
      setSwipeOffset((prevOffset) => {
        if (swipeStartX === null) return prevOffset;
        const diff = clientX - swipeStartX;
        const isRTL = i18n.language === "ar";

        // In LTR: swiping right = positive diff
        // In RTL: swiping right (visually) = negative diff (moving left in screen coords)
        // We want to track the absolute distance swiped
        const swipeDistance = isRTL ? Math.abs(diff) : diff;

        // Only allow swiping right (positive distance)
        if (swipeDistance > 0 && swipeDistance <= 400) {
          return swipeDistance;
        } else if (swipeDistance > 400) {
          return 400;
        }
        return prevOffset;
      });
    },
    [swipeStartX, i18n.language]
  );

  const handleSwipeEnd = useCallback(() => {
    if (swipeStartX === null || isProcessingRef.current) return;

    setSwipeOffset((prevOffset) => {
      if (prevOffset <= 100) {
        // Not enough swipe, just reset
        setSwipeStartX(null);
        return 0;
      }

      // Mark as processing to prevent duplicate calls
      isProcessingRef.current = true;
      setSwipeStartX(null);

      const nextIndex = currentStepIndex + 1;
      if (nextIndex >= steps.length) {
        // All steps completed, go to rules
        setStep("rules");
        setTimeout(() => {
          isProcessingRef.current = false;
        }, 100);
        setCurrentStepIndex(currentPlayerIndex);
      } else {
        setCurrentStepIndex(nextIndex);
        setTimeout(() => {
          setSwipeOffset(0);
          isProcessingRef.current = false;
        }, 300);
      }

      return prevOffset; // Keep offset during processing
    });
  }, [swipeStartX, steps.length, currentStepIndex, currentPlayerIndex]);

  const handleTouchStart = (e: React.TouchEvent) => {
    handleSwipeStart(e.touches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (swipeStartX === null) return;
    e.preventDefault(); // Prevent scrolling while swiping
    handleSwipeMove(e.touches[0].clientX);
  };

  const handleTouchEnd = () => {
    handleSwipeEnd();
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    handleSwipeStart(e.clientX);
  };

  // Global mouse handlers for dragging
  useEffect(() => {
    const handleGlobalMouseMove = (e: MouseEvent) => {
      if (swipeStartX !== null) {
        handleSwipeMove(e.clientX);
      }
    };

    const handleGlobalMouseUp = () => {
      if (swipeStartX !== null) {
        handleSwipeEnd();
      }
    };

    if (swipeStartX !== null) {
      document.addEventListener("mousemove", handleGlobalMouseMove);
      document.addEventListener("mouseup", handleGlobalMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleGlobalMouseMove);
      document.removeEventListener("mouseup", handleGlobalMouseUp);
    };
  }, [swipeStartX, handleSwipeMove, handleSwipeEnd]);

  // Reset swipe state when step changes
  useEffect(() => {
    if (step === "reveal") {
      setSwipeOffset(0);
      setSwipeStartX(null);
      isProcessingRef.current = false;
    }
  }, [step, currentStepIndex]);

  // Loading state
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          background: `linear-gradient(135deg, ${(theme.palette.background as any).gradient.start} 0%, ${(theme.palette.background as any).gradient.middle} 50%, ${(theme.palette.background as any).gradient.end} 100%)`,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            color: theme.palette.text.primary,
            fontWeight: 600,
          }}
        >
          {t("common.loading")}
        </Typography>
      </Box>
    );
  }

  // Error state
  if (error || !content) {
    const neonPalette = (theme.palette as any).neon;
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          background: `linear-gradient(135deg, ${(theme.palette.background as any).gradient.start} 0%, ${(theme.palette.background as any).gradient.middle} 50%, ${(theme.palette.background as any).gradient.end} 100%)`,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            color: neonPalette.pink,
            fontWeight: 600,
          }}
        >
          {error || t("common.error")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "calc(100vh)",
        background: `linear-gradient(135deg, ${(theme.palette.background as any).gradient.start} 0%, ${(theme.palette.background as any).gradient.middle} 50%, ${(theme.palette.background as any).gradient.end} 100%)`,
        py: 4,
      }}
    >
      <Container maxWidth="md">
        {/* Content Info - Always visible */}
        {content && (
          <ImposterContentInfo content={content} getImageUrl={getImageUrl} />
        )}

        {/* Setup Step */}
        {step === "setup" && (
          <ImposterSetupStep
            numPlayers={numPlayers}
            numImposters={numImposters}
            onNumPlayersChange={setNumPlayers}
            onPlay={handlePlay}
          />
        )}

        {/* Reveal Step */}
        {step === "reveal" && content && (
          <ImposterRevealStep
            currentPlayerNumber={currentPlayerNumber}
            isWaiting={isWaiting}
            isCurrentPlayerImposter={isCurrentPlayerImposter}
            content={content}
            contentImage={contentImage}
            imposterModeIcon={null}
            swipeOffset={swipeOffset}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onMouseDown={handleMouseDown}
          />
        )}

        {/* Pass to Next Player Step */}
        {step === "pass" && (
          <ImposterPassStep
            currentPlayerIndex={currentPlayerIndex}
            onNext={() => {
              setCurrentStepIndex((prev) => {
                const next = prev + 1;
                if (next >= steps.length) {
                  setStep("rules");
                  return prev;
                }
                return next;
              });
            }}
          />
        )}

        {/* Rules Step */}
        {step === "rules" && <ImposterRulesStep />}
      </Container>
    </Box>
  );
};

export default ImposterPage;
